import { useScenarioForm } from './contexts';
import styles from './global-configs.module.scss';
import {
  DropdownField,
  TextField,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import { StatusDefinition } from './types';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { Edit, Information } from '@carbon/react/icons';
import { Link, SkeletonText, Tooltip } from '@carbon/react';
import sectionStyles from './config-sections.module.scss';
import { useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { dcaScenariosListAtom, fetchDcaScenariosAtom } from './atoms';
import { DcaScenarioSelectionModal } from './dca-scenario-selection-modal';

const STATUS_OPTIONS: StatusDefinition[] = [
  'eval_upside',
  'primary_secondary',
  'proved_probable_possible',
];

const STATUS_LABELS: Record<StatusDefinition, string> = {
  eval_upside: 'Eval/Upside',
  primary_secondary: 'Primary/Secondary',
  proved_probable_possible: 'Proved/Probable/Possible',
};

const statusToLabel = (status: string | null) => {
  if (!status) return '';
  return STATUS_LABELS[status as StatusDefinition] || status;
};

export const GlobalConfigs = () => {
  const { form, readOnly, setFieldBaseline } = useScenarioForm();
  const [dcaDrawerOpen, setDcaDrawerOpen] = useState(false);
  const fetchDcaScenarios = useSetAtom(fetchDcaScenariosAtom);
  const dcaScenariosList = useAtomValue(dcaScenariosListAtom);

  useEffect(() => {
    fetchDcaScenarios();
  }, [fetchDcaScenarios]);

  // Auto-select "PDP default" DCA scenario for new type curve scenarios
  useEffect(() => {
    if (dcaScenariosList.loading || dcaScenariosList.data.length === 0) return;
    // Only auto-select if no DCA scenario is set yet (new scenario)
    if (form.state.values.globalConfig.dcaScenarioId) return;

    const pdpScenario = dcaScenariosList.data.find(
      (s) => s.name.toLowerCase() === 'pdp default'
    );
    if (pdpScenario) {
      const current = form.state.values.globalConfig;
      const updatedGlobalConfig = {
        ...current,
        dcaScenarioId: String(pdpScenario.id),
        dcaScenarioName: pdpScenario.name,
      };
      setFieldBaseline('globalConfig', updatedGlobalConfig);
    }
  }, [dcaScenariosList.loading, dcaScenariosList.data, form, setFieldBaseline]);

  return (
    <>
      <Accordion defaultExpanded>
        <AccordionSummary className={sectionStyles.accordionSummaryBorder}>
          <h3 className={sectionStyles.sectionTitle}>Scenario Details</h3>
        </AccordionSummary>
        <AccordionDetails>
          <div className={sectionStyles.sectionDetails}>
            <p className={styles.sectionDescription}>
              Top-level scenario configs including name and status definitions.
            </p>

            <div className={styles.inputsContainer}>
              <form.Field name="globalConfig.name">
                {(field) => (
                  <TanstackFieldWrapper<string> field={field}>
                    {(props) => (
                      <TextField
                        id="scenarioName"
                        labelText="Type Curve Scenario Name (required)"
                        placeholder=""
                        disabled={readOnly}
                        {...props}
                      />
                    )}
                  </TanstackFieldWrapper>
                )}
              </form.Field>

              <form.Subscribe
                selector={(state) => ({
                  dcaScenarioId: state.values.globalConfig.dcaScenarioId,
                  dcaScenarioName: state.values.globalConfig.dcaScenarioName,
                })}
              >
                {({ dcaScenarioId, dcaScenarioName }) => (
                  <div>
                    <div className={styles.fieldLabel}>
                      Well Control DCA Scenario
                    </div>
                    {dcaScenariosList.loading ? (
                      <SkeletonText className={styles.dcaScenarioSkeleton} />
                    ) : (
                      <div className={styles.dcaScenarioLink}>
                        {dcaScenarioName ? (
                          <>
                            <Link
                              href={
                                dcaScenarioId
                                  ? `/dca_scenarios/${dcaScenarioId}`
                                  : undefined
                              }
                              target="_blank"
                            >
                              {dcaScenarioName}
                            </Link>
                            {!readOnly && (
                              <button
                                type="button"
                                className={styles.editIconButton}
                                onClick={() => setDcaDrawerOpen(true)}
                              >
                                <Edit size={16} />
                              </button>
                            )}
                          </>
                        ) : !readOnly ? (
                          <button
                            type="button"
                            className={styles.editIconButton}
                            onClick={() => setDcaDrawerOpen(true)}
                          >
                            <Edit size={16} />
                            <span>Select DCA Scenario</span>
                          </button>
                        ) : (
                          <span className={styles.dcaScenarioEmpty}>
                            No DCA Scenario linked
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </form.Subscribe>

              <form.Field name={'globalConfig.status' as any}>
                {(field) => (
                  <TanstackFieldWrapper<StatusDefinition | null> field={field}>
                    {(props) => (
                      <div className={styles.statusFieldWrapper}>
                        <DropdownField
                          id="scenarioStatus"
                          label="Select status"
                          titleText={
                            <span className={styles.labelWithIcon}>
                              Status Definition (Required)
                              <Tooltip
                                align="top"
                                label="Defines the Type Curve Status classifications for Undeveloped Locations in this Type Curve Scenario. The Type Curve Status can be used downstream to assign other Scenario configs (i.e. discount rates, scheduling, risking etc). At least one status must be defined."
                              >
                                <Information size={16} />
                              </Tooltip>
                            </span>
                          }
                          items={STATUS_OPTIONS}
                          itemToString={(item: string | null) =>
                            statusToLabel(item)
                          }
                          disabled={readOnly}
                          {...props}
                        />
                      </div>
                    )}
                  </TanstackFieldWrapper>
                )}
              </form.Field>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      <DcaScenarioSelectionModal
        open={dcaDrawerOpen}
        onClose={() => setDcaDrawerOpen(false)}
        currentDcaScenarioId={form.state.values.globalConfig.dcaScenarioId}
        onSelect={(scenario) => {
          const current = form.state.values.globalConfig;
          form.setFieldValue('globalConfig', {
            ...current,
            dcaScenarioId: String(scenario.id),
            dcaScenarioName: scenario.name,
          });
        }}
      />
    </>
  );
};
